import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Plus, Check, X, Loader2, Languages } from 'lucide-react'
import { getDocumentListItems, addDocumentListItem, removeDocumentListItem, translateJournalTitle } from '../../services/api'
import { repairMojibake } from '../../utils/textRepair'
import type { Document, DocumentListItem } from '../../types'

interface DocumentJournalsPanelProps {
  document: Document
}

interface RelatedJournal {
  journal_id: string
  title: string
  authors?: string[]
  doi?: string
  similarity_score: number
}

// LANGDETECT-C5: client-side Unicode-range language detector (no network, instant).
function detectScript(text: string): { code: string; label: string } | null {
  for (const char of text) {
    const cp = char.codePointAt(0) ?? 0
    if (cp >= 0x0530 && cp <= 0x058F) return { code: 'hy', label: 'ARM' }
    if ((cp >= 0x4E00 && cp <= 0x9FFF) || (cp >= 0x3400 && cp <= 0x4DBF)) return { code: 'zh', label: 'ZH' }
    if (cp >= 0x0600 && cp <= 0x06FF) return { code: 'ar', label: 'AR' }
    if (cp >= 0x0400 && cp <= 0x04FF) return { code: 'ru', label: 'CYR' }
    if (cp >= 0x0590 && cp <= 0x05FF) return { code: 'he', label: 'HE' }
    if (cp >= 0xAC00 && cp <= 0xD7AF) return { code: 'ko', label: 'KO' }
    if (cp >= 0x3040 && cp <= 0x30FF) return { code: 'ja', label: 'JA' }
  }
  return null
}

function LanguageBadge({ label, onClick, loading }: { label: string; onClick: () => void; loading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      title={`Translate from ${label}`}
      className="flex items-center gap-0.5 px-1 py-0.5 text-xs rounded bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50"
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
      {label}
    </button>
  )
}

function ScoreBadge({ score }: { score: number }) {
  const color = score > 0.8 ? 'bg-green-100 text-green-700' : score > 0.6 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
  return <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${color}`}>{(score * 100).toFixed(0)}%</span>
}

export default function DocumentJournalsPanel({ document }: DocumentJournalsPanelProps) {
  const queryClient = useQueryClient()
  const [translations, setTranslations] = useState<Record<string, string>>({})
  const [translating, setTranslating] = useState<string | null>(null)

  const { data: savedData, isLoading: loadingSaved } = useQuery({
    queryKey: ['document-list', document.document_id, 'journal'],
    queryFn: () => getDocumentListItems(document.document_id, 'journal'),
  })

  const saveMutation = useMutation({
    mutationFn: (journal: RelatedJournal) =>
      addDocumentListItem(document.document_id, {
        resource_type: 'journal',
        resource_id: journal.journal_id,
        title: journal.title,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-list', document.document_id, 'journal'] }),
  })

  const removeMutation = useMutation({
    mutationFn: (item: DocumentListItem) =>
      removeDocumentListItem(document.document_id, 'journal', item.resource_id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-list', document.document_id, 'journal'] }),
  })

  const savedIds = useMemo(
    () => new Set((savedData?.items || []).map((i) => i.resource_id)),
    [savedData]
  )

  const relatedJournals = useMemo(() => {
    const journalMap = new Map<string, RelatedJournal>()
    const related = document.related_journals
    if (!related) return []
    for (const j of related) {
      const journal: RelatedJournal = {
        journal_id: j.id,
        title: repairMojibake(j.title),
        authors: j.authors,
        doi: j.doi,
        similarity_score: j.score ?? 0,
      }
      const existing = journalMap.get(j.id)
      if (!existing || journal.similarity_score > existing.similarity_score) {
        journalMap.set(j.id, journal)
      }
    }
    return Array.from(journalMap.values()).sort((a, b) => b.similarity_score - a.similarity_score)
  }, [document])

  const handleTranslate = async (journal: RelatedJournal, scriptCode: string) => {
    setTranslating(journal.journal_id)
    try {
      const result = await translateJournalTitle(journal.title, scriptCode)
      setTranslations((prev) => ({ ...prev, [journal.journal_id]: result.translated_text }))
    } finally {
      setTranslating(null)
    }
  }

  return (
    <div className="space-y-4">
      {/* Related (AI-discovered) */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Related Journals <span className="text-xs text-gray-500">(AI-discovered via embeddings)</span>
        </h4>
        {relatedJournals.length === 0 ? (
          <p className="text-sm text-gray-500">
            {document.journal_discovery_status === 'completed' || document.journal_discovery_status === 'no_results'
              ? 'No related journals found for this document.'
              : document.journal_discovery_status === 'skipped'
                ? 'Journal discovery was skipped for this document type.'
                : 'No related journals found. Embeddings must complete first.'}
          </p>
        ) : (
          <div className="space-y-2">
            {relatedJournals.map((j) => {
              const script = detectScript(j.title)
              const translated = translations[j.journal_id]
              return (
                <div key={j.journal_id} className="flex items-start gap-3 p-2.5 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{j.title}</p>
                    {translated && (
                      <p className="text-xs text-blue-600 truncate">{translated}</p>
                    )}
                    {j.authors && j.authors.length > 0 && (
                      <p className="text-xs text-gray-500 truncate">
                        {j.authors.slice(0, 3).join(', ')}
                        {j.authors.length > 3 && ` +${j.authors.length - 3}`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <ScoreBadge score={j.similarity_score} />
                    {script && !translated && (
                      <LanguageBadge
                        label={script.label}
                        onClick={() => handleTranslate(j, script.code)}
                        loading={translating === j.journal_id}
                      />
                    )}
                    {j.doi && (
                      <a href={`https://doi.org/${j.doi}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-iflow-500">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {savedIds.has(j.journal_id) ? (
                      <span className="flex items-center gap-0.5 text-xs text-green-600">
                        <Check className="w-3 h-3" /> Saved
                      </span>
                    ) : (
                      <button
                        onClick={() => saveMutation.mutate(j)}
                        disabled={saveMutation.isPending}
                        className="flex items-center gap-0.5 text-xs text-iflow-600 hover:text-iflow-700"
                      >
                        <Plus className="w-3 h-3" /> Save
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Saved Journals */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Saved Journals <span className="text-xs text-gray-500">(persisted to this document)</span>
        </h4>
        {loadingSaved ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>
        ) : (savedData?.items || []).length === 0 ? (
          <p className="text-sm text-gray-500">No saved journals yet. Click + Save above to persist a connection.</p>
        ) : (
          <div className="space-y-2">
            {(savedData?.items || []).map((item) => (
              <div key={item.sort_key} className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{item.title}</p>
                </div>
                <button
                  onClick={() => removeMutation.mutate(item)}
                  disabled={removeMutation.isPending}
                  className="p-1 text-gray-500 hover:text-red-500 rounded"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
